import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Plus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export type AgentEscalationReturnMode =
  | "auto_after_scheduling"
  | "auto_after_task"
  | "explicit";

export type AgentEscalationTaskType =
  | "scheduling"
  | "technical"
  | "sales"
  | "general";

export interface AgentEscalationRule {
  condition: string;
  target_agent_id: string;
  label?: string;
  return_mode?: AgentEscalationReturnMode;
  task_type?: AgentEscalationTaskType;
}

interface AgentOption {
  id: string;
  name: string;
  is_active: boolean;
}

const CUSTOM_RULE_VALUE = "__custom__";

type RuleSuggestion = {
  label: string;
  value: string;
  task_type?: AgentEscalationTaskType;
  return_mode?: AgentEscalationReturnMode;
  rule_label?: string;
};

const ESCALATION_RULE_SUGGESTIONS: RuleSuggestion[] = [
  {
    label: "Agendamento — profissional A (barbearia)",
    rule_label: "Agenda Profissional A",
    task_type: "scheduling",
    return_mode: "auto_after_scheduling",
    value:
      "Transfira quando o cliente quiser agendar, remarcar ou cancelar com o profissional A (use a agenda/calendário desse profissional).",
  },
  {
    label: "Agendamento — profissional B (barbearia)",
    rule_label: "Agenda Profissional B",
    task_type: "scheduling",
    return_mode: "auto_after_scheduling",
    value:
      "Transfira quando o cliente quiser agendar, remarcar ou cancelar com o profissional B (use a agenda/calendário desse profissional).",
  },
  {
    label: "Dúvida técnica especializada",
    task_type: "technical",
    return_mode: "explicit",
    value:
      "Transfira quando o cliente tiver dúvidas técnicas, de implementação ou configuração que exijam conhecimento aprofundado fora do seu escopo.",
  },
  {
    label: "Agendamento / consultoria",
    task_type: "scheduling",
    return_mode: "auto_after_scheduling",
    value:
      "Transfira para o agente de agendamentos quando o cliente quiser marcar reunião, demonstração ou consultoria que exija agenda específica.",
  },
  {
    label: "Vendas / orçamento complexo",
    task_type: "sales",
    return_mode: "explicit",
    value:
      "Transfira para o agente de vendas quando o cliente pedir proposta comercial, orçamento detalhado ou negociação que exija o especialista comercial.",
  },
  {
    label: "Suporte pós-venda",
    task_type: "general",
    return_mode: "explicit",
    value:
      "Transfira para o agente de suporte quando o cliente já é cliente e precisa de ajuda com uso, entrega, garantia ou pós-venda.",
  },
  {
    label: "Fora do escopo do agente",
    task_type: "general",
    return_mode: "explicit",
    value:
      "Transfira para outro agente somente quando a solicitação estiver claramente fora do seu escopo e outro agente do workspace for o responsável por esse tema.",
  },
];

const RETURN_MODE_LABELS: Record<AgentEscalationReturnMode, string> = {
  auto_after_scheduling: "Automático após agendar/remarcar/cancelar",
  auto_after_task: "Automático após concluir tarefa",
  explicit: "Explícito (especialista chama return_to_source_agent)",
};

const TASK_TYPE_LABELS: Record<AgentEscalationTaskType, string> = {
  scheduling: "Agendamento",
  technical: "Técnico",
  sales: "Vendas",
  general: "Geral",
};

interface AgentEscalationRuleInputProps {
  rules: AgentEscalationRule[];
  onChange: (rules: AgentEscalationRule[]) => void;
  workspaceId: string;
  currentAgentId?: string;
  maxItems?: number;
}

export function AgentEscalationRuleInput({
  rules,
  onChange,
  workspaceId,
  currentAgentId,
  maxItems = 10,
}: AgentEscalationRuleInputProps) {
  const [agents, setAgents] = useState<AgentOption[]>([]);

  const fetchAgents = useCallback(async () => {
    const { data } = await supabase
      .from("agents")
      .select("id, name, is_active")
      .eq("workspace_id", workspaceId)
      .order("name");

    if (data) {
      setAgents(data);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchAgents();
  }, [fetchAgents]);

  const availableTargets = agents.filter(
    (a) => a.id !== currentAgentId && a.is_active,
  );

  const addRule = () => {
    if (rules.length < maxItems) {
      const defaultTarget = availableTargets[0]?.id || "";
      onChange([
        ...rules,
        {
          condition: "",
          target_agent_id: defaultTarget,
          return_mode: "explicit",
          task_type: "general",
        },
      ]);
    }
  };

  const updateRule = (index: number, updates: Partial<AgentEscalationRule>) => {
    const updated = [...rules];
    updated[index] = { ...updated[index], ...updates };
    onChange(updated);
  };

  const removeRule = (index: number) => {
    onChange(rules.filter((_, i) => i !== index));
  };

  const getSelectedSuggestion = (rule: AgentEscalationRule) => {
    return ESCALATION_RULE_SUGGESTIONS.find((suggestion) => suggestion.value === rule.condition)
      ? rule.condition
      : CUSTOM_RULE_VALUE;
  };

  const handleSuggestionChange = (index: number, value: string) => {
    if (value === CUSTOM_RULE_VALUE) {
      if (getSelectedSuggestion(rules[index]) !== CUSTOM_RULE_VALUE) {
        updateRule(index, { condition: "" });
      }
      return;
    }

    const suggestion = ESCALATION_RULE_SUGGESTIONS.find((s) => s.value === value);
    updateRule(index, {
      condition: value,
      ...(suggestion?.rule_label ? { label: suggestion.rule_label } : {}),
      ...(suggestion?.task_type ? { task_type: suggestion.task_type } : {}),
      ...(suggestion?.return_mode ? { return_mode: suggestion.return_mode } : {}),
    });
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Configure o agente principal como hub: cada regra encaminha para um especialista e define como o atendimento volta.
      </p>

      {rules.length === 0 ? (
        <p className="text-sm text-muted-foreground italic py-2">Nenhuma regra configurada</p>
      ) : (
        rules.map((rule, index) => {
          const targetInvalid =
            !rule.target_agent_id ||
            rule.target_agent_id === currentAgentId ||
            !availableTargets.some((a) => a.id === rule.target_agent_id);

          return (
            <div key={index} className="relative border border-border rounded-lg p-3 space-y-3 agent-field-enter bg-muted/30">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 space-y-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Sugestão de regra</Label>
                    <Select
                      value={getSelectedSuggestion(rule)}
                      onValueChange={(val) => handleSuggestionChange(index, val)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Escolha uma regra ou personalize" />
                      </SelectTrigger>
                      <SelectContent>
                        {ESCALATION_RULE_SUGGESTIONS.map((suggestion) => (
                          <SelectItem key={suggestion.label} value={suggestion.value}>
                            {suggestion.label}
                          </SelectItem>
                        ))}
                        <SelectItem value={CUSTOM_RULE_VALUE}>Personalizada</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Rótulo interno (opcional)</Label>
                    <Input
                      value={rule.label || ""}
                      onChange={(e) => updateRule(index, { label: e.target.value })}
                      placeholder="Ex: Agenda do João"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Condição personalizada</Label>
                    <Input
                      value={rule.condition}
                      onChange={(e) => updateRule(index, { condition: e.target.value })}
                      placeholder="Ex: Transfira quando o cliente pedir agendamento com o João."
                      className="mt-1"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Tipo de tarefa</Label>
                      <Select
                        value={rule.task_type || "general"}
                        onValueChange={(val) => updateRule(index, { task_type: val as AgentEscalationTaskType })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(TASK_TYPE_LABELS) as AgentEscalationTaskType[]).map((key) => (
                            <SelectItem key={key} value={key}>
                              {TASK_TYPE_LABELS[key]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Modo de retorno</Label>
                      <Select
                        value={rule.return_mode || "explicit"}
                        onValueChange={(val) => updateRule(index, { return_mode: val as AgentEscalationReturnMode })}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(RETURN_MODE_LABELS) as AgentEscalationReturnMode[]).map((key) => (
                            <SelectItem key={key} value={key}>
                              {RETURN_MODE_LABELS[key]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs text-muted-foreground">Agente destino</Label>
                    <Select
                      value={rule.target_agent_id || undefined}
                      onValueChange={(val) => updateRule(index, { target_agent_id: val })}
                    >
                      <SelectTrigger className={`mt-1 ${targetInvalid ? "border-destructive" : ""}`}>
                        <SelectValue placeholder="Selecione o agente especialista" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTargets.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {targetInvalid && (
                      <p className="mt-1 text-[11px] text-destructive">
                        Selecione um agente ativo diferente de você.
                      </p>
                    )}
                  </div>
                </div>

                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeRule(index)}
                  className="flex-shrink-0 text-muted-foreground hover:text-foreground hover:bg-muted/80 rounded-md mt-4"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          );
        })
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRule}
        disabled={rules.length >= maxItems || availableTargets.length === 0}
        className="w-full border-dashed"
      >
        <Plus className="w-4 h-4 mr-2" /> Adicionar Regra
      </Button>

      {availableTargets.length === 0 && (
        <p className="text-xs text-muted-foreground text-center">
          É necessário ter outro agente ativo no workspace para configurar escalação.
        </p>
      )}

      {rules.length >= maxItems && (
        <p className="text-xs text-muted-foreground text-center">
          Limite de {maxItems} regras atingido
        </p>
      )}
    </div>
  );
}
